import events from "_src/engine/events";
import output from "_src/engine/output";
import {BufferReaderType} from "_src/client/buffer_reader";

import workspace from "_src/workspace";
import appState from "_src/app_state";

import windowManager from "_src/client/window_manager";
import {colorUtils} from "_src/utils";
import MeasureLineWidget from "_src/client/widgets/measure_line";
import SectionWidget, {finishSectionSelection, prepareSectionSelection} from "_src/client/widgets/section";
import SectionAnalysisWidget from "_src/client/widgets/section_analysis";
import {objectType} from "_src/constants";
import renderGraph from "_src/render_graph/render_graph";
import objState from "_src/obj_state";
import {GeometryBuffer} from "_src/engine/geometry";
import {getAttributeIDByName, getTypeByID, getTypedArrayByID} from "_src/engine/utility/attributes";
import selector from "_src/manipulators/libs/selector";
import projectTree from "_src/reformer/project_tree";

let _currentDistanceMapId = null;
let _currentDistanceMapObjectId = null;
let _currentDistanceMapHash = null;
let _lineWidgets = new Map();
let _sectionWidgets = new Map();
let _sectionAnalysisWidgets = new Map();

let _savedDistanceMapColors = [0,0,0,255];

const INVALID_ID = "";

const _fakeAnalysisData = [
    //createProperty(propertyType.LEVEL),
    //createProperty(propertyType.LEVEL),
    ];
/*
_fakeAnalysisData[0].setValue("id", "id1");
_fakeAnalysisData[0].setValue("dirty", false);
_fakeAnalysisData[0].setValue("visible", true);
_fakeAnalysisData[0].setValue("geometries", [createProperty(propertyType.LEVEL)]);

_fakeAnalysisData[1].setValue("id", "id2");
_fakeAnalysisData[1].setValue("dirty", false);
_fakeAnalysisData[1].setValue("visible", true);
_fakeAnalysisData[1].setValue("geometries", [createProperty(propertyType.LEVEL)]);
*/

export default {
  cleanup() {
    _cleanLines();
    _cleanSections();
    _cleanDistanceMap();
    _cleanSectionAnalysis();
  },

  calculateDistanceMapHistogram(distance, histogramSize, curveSize) {
    if (!_currentDistanceMapId) {
      output.warn("Can't calculate distance map histogram. Distance map is not visible.");
      return null;
    }

    const currentDistanceMap = workspace.getMeasureByID(_currentDistanceMapId);
    const properties = currentDistanceMap.getProperty("properties");
    const histogramData = {
      "histogram": _calculateHistogram(distance, histogramSize),
      "curve": _calculateCurve(distance, properties["mean-signed-distance"], properties["root-mean-square"], curveSize),
      "ticks": _calculateTicks(properties["mean-signed-distance"], properties["root-mean-square"]),
    };

    return histogramData;
  },

  setDistanceMapRenderingProperties(colors, thrColor, distance, lowerThr, upperThr, continuousMode) {
    if (!_currentDistanceMapId) {
      output.warn("Can't set distance map properties. Distance map is not visible.");
      return;
    }

    const currentDistanceMap = workspace.getMeasureByID(_currentDistanceMapId);
    const distanceMapObject = workspace.getObjectByID(_currentDistanceMapObjectId);

    const searchDistance = currentDistanceMap.getProperty("searchDistance");

    const minDistance = -distance;
    const maxDistance = distance;
    const range = maxDistance - minDistance;
    const minThr = minDistance + range * lowerThr;
    const maxThr = minDistance + range * upperThr;

    const newDistanceMapColors = [];
    for (let j = 0; j < colors.length; ++j) {
      const color = colorUtils.hexToRgb(colors[j]);
      newDistanceMapColors.push(color.r * 255, color.g * 255, color.b * 255, 255);
    }
    _updateDistanceMapColors(newDistanceMapColors);

    const rgbThrColor = colorUtils.hexToRgb(thrColor);
    distanceMapObject.model.shader.setThrColor(rgbThrColor.r, rgbThrColor.g, rgbThrColor.b);
    distanceMapObject.model.shader.setDistanceGradientRange(minDistance, maxDistance);
    distanceMapObject.model.shader.setDistanceThrRange(minThr, maxThr);
    distanceMapObject.model.shader.setDistanceSearchRange(-searchDistance, searchDistance);
    distanceMapObject.model.shader.setDirective("CONTINUOUS_DISTANCE", continuousMode);

    const mainWindow = windowManager.getWindow(windowManager.MAIN);
    mainWindow.renderGraph.requestFrame();
    mainWindow.bufferReader.dropBuffers();
  },

  updateScreenAppearance() {
    _updateScreenAppearance();
  },

  async checkDistanceMapPoint(screenX, screenY) {
    if (!_currentDistanceMapId) {
      return;
    }

    const mainWindow = windowManager.getWindow(windowManager.MAIN);
    if (!mainWindow.isInViewport(screenX, screenY)) {
      events.emit("distanceMapDistance", null);
      return;
    }

    const currentDistanceMap = workspace.getMeasureByID(_currentDistanceMapId);
    const searchDistance = currentDistanceMap.getProperty("searchDistance");
    const textureX = mainWindow.toViewportX(screenX);
    const textureY = mainWindow.toViewportY(screenY);

    const res = await mainWindow.bufferReader.readPixelCache(textureX, textureY, BufferReaderType.GENERAL_PURPOSE);
    if (res < 0) {
      events.emit("distanceMapDistance", null);
      return;
    }
    const minDistance = -searchDistance;
    const maxDistance = searchDistance;

    const distance = minDistance + (maxDistance - minDistance) * res;
    events.emit("distanceMapDistance", distance);
  },

  async checkSectionAnalysisPoint(screenX, screenY) {
    if (_sectionAnalysisWidgets.size === 0) return;

    const mainWindow = windowManager.getWindow(windowManager.MAIN);
    if (!mainWindow.isInViewport(screenX, screenY)) {
      events.emit("sectionAnalysisDistance", null);
      return;
    }

    const textureX = mainWindow.toViewportX(screenX);
    const textureY = mainWindow.toViewportY(screenY);
    const res = await mainWindow.bufferReader.readPixelCache(textureX, textureY, BufferReaderType.SECTION_ANALYSIS);
    if (res < 0) {
      events.emit("sectionAnalysisDistance", null);
      return;
    }

    const sectionAnalysisProperty = _fakeAnalysisData[0];
    const distanceRange = sectionAnalysisProperty.getValue("geometries")[0].getValue("data").distanceRange;
    const distance = distanceRange[0] + (distanceRange[1] - distanceRange[0]) * res;

    events.emit("sectionAnalysisDistance", distance);
  },

  dropSelections() {
    for (const measure of workspace.getMeasures()) {
      projectTree.setPropertyValue(measure.getID(), "selected", false);
    }

    for (const sectionGroup of workspace.getMeasures().filter(measure => measure.type === objectType.SECTION)) {
      for (let i=0; i<sectionGroup.sections.length; i++) {
        const section = sectionGroup.sections[i];
        projectTree.setSubSectionProperty(sectionGroup.getID(), i, undefined, "selected", false);
        for (let j=0; j<section.contours.length; j++){
          projectTree.setSubSectionProperty(sectionGroup.getID(), i, j, "selected", false);
        }
      }
    }
  },

  setMeasureSelection(measureID, sectionIDx, contourIDx, selection) {
    const measureObject = workspace.getMeasureByID(measureID);
    if (!measureObject) {
      output.error(output.API_ERROR, "Measure with id " + measureID + " is not found");
      return;
    }

    if (sectionIDx === undefined && contourIDx === undefined) {
      if (measureObject.type !== objectType.DISTANCE_MAP) {
        measureObject.setSelection(selection);
      }
    } else {
      if (measureObject.type === objectType.SECTION) {
        projectTree.setSubSectionProperty(measureID, sectionIDx, contourIDx, "selected", selection);
      }
    }
  },

  isAnySectionVisible() {
    const isAnyVisible = Boolean(workspace.getMeasures().find(measure => measure.type === objectType.SECTION && measure.getVisibility()));
    return isAnyVisible;
  },

  async update() {
    await _updateVisibility();
    await _updateSelection();
  },

  addDistanceMapToObject,

  addTemporalMeasure(measure) {
    switch (measure.type) {
      case objectType.LINEAR_MEASURE:
      case objectType.GEODESIC_MEASURE: {
        const widget = new MeasureLineWidget(measure);
        _lineWidgets.set(INVALID_ID, widget);
        _addLineWidgetToScenes(widget);

        break;
      }
    }

    _updateScreenAppearance();
  },

  removeTemporalMeasure(type) {
    switch (type) {
      case objectType.LINEAR_MEASURE:
      case objectType.GEODESIC_MEASURE: {
        const widget = _lineWidgets.get(INVALID_ID);
        if (widget) {
          _lineWidgets.delete(INVALID_ID);
          _removeLineWidgetFromScenes(widget);
        }

        break;
      }
    }
  },

  getTemporalMeasure(type) {
    switch (type) {
      case objectType.LINEAR_MEASURE:
      case objectType.GEODESIC_MEASURE:
        return _lineWidgets.get(INVALID_ID);
    }

    return null;
  },

}

async function _createSectionAnalysisWidget(property) {
  const geometry = property.getValue("geometries")[0];
  await _initSectionAnalysisGeometry(geometry);
  const geometryData = geometry ? geometry.getValue("data") : {};

  const sectionAnalysisWidget = new SectionAnalysisWidget(geometryData);
  return sectionAnalysisWidget;
}

const _fakeDataGen = (() => {
  const data = [
    {
      contour1: [0,0,0, 0,1,0, 1,1,1, 1,2,1],
      contour2: [-1,0,0, -1,1,0, 0,1,1, 0,2,1],
      whisker1: [0,0,0, 0,1,0, 1,1,1, 1,2,1],
      whisker2: [-1,0,0, -1,1,0, 0,1,1, 0,2,1],
      color1: [0,0,1],
      color2: [1,0,0],
      distances: [-0.5, 1.5, 0.5, 3],
      tolerance: 0.6,
      distanceRange: [-0.5, 3.0],
    },
    {
      contour1: [10, 10, 10, 10, 11, 10, 11, 11, 11, 11, 12, 11],
      contour2: [-1, 10, 10, -1, 11, 10, 10, 11, 11, 10, 12, 11],
      whisker1: [10, 10, 10, 10, 11, 10, 11, 11, 11, 11, 12, 11],
      whisker2: [-1, 10, 10, -1, 11, 10, 10, 11, 11, 10, 12, 11],
      color1: [0,0,1],
      color2: [1,0,0],
      distances: [1.5, 0.05, 0.5, 1],
      tolerance: 1.0,
      distanceRange: [0.05, 1.5],
    }
  ];
  let iteration = 0;

  return function() {
    return data[iteration++];
  }
})();

async function _initSectionAnalysisGeometry(geometry) {
  if (!geometry) return;

  const data = _fakeDataGen();

  geometry.setValue("data", data);
}

function _addLineWidgetToScenes(widget) {
  const graph = windowManager.getWindow(windowManager.MAIN).renderGraph;
  const measureLinesPass = graph.getPass(renderGraph.MEASURE_LINES);
  const linesScene = measureLinesPass.getSecondScene();
  const textScene = measureLinesPass.getScenes()[6];
  const fakeTransparentScene = textScene;
  widget.addToScenes(linesScene, textScene);
  widget.setSelection(true, linesScene, fakeTransparentScene);

  graph.requestFrame();
}

function _removeLineWidgetFromScenes(widget) {
  const graph = windowManager.getWindow(windowManager.MAIN).renderGraph;
  const measureLinesPass = graph.getPass(renderGraph.MEASURE_LINES);
  const linesScene = measureLinesPass.getSecondScene();
  const textScene = measureLinesPass.getScenes()[6];
  widget.removeFromScenes(linesScene, textScene);

  graph.requestFrame();
}

function _addSectionWidgetToScenes(widget) {
  const graph = windowManager.getWindow(windowManager.MAIN).renderGraph;

  const sectionsScene = graph.getInterfaceScene();
  for (const model of widget.getModels()) {
    sectionsScene.add(model);
  }

  graph.requestFrame();
}

function _removeSectionWidgetFromScenes(widget) {
  const graph = windowManager.getWindow(windowManager.MAIN).renderGraph;

  const sectionsScene = graph.getInterfaceScene();
  for (const model of widget.getModels()) {
    sectionsScene.remove(model);
  }

  graph.requestFrame();
}

function _addSectionAnalysisWidgetToScenes(widget) {
  const graph = windowManager.getWindow(windowManager.MAIN).renderGraph;
  const linesScene = graph.getInterfaceScene();
  const picksScene = graph.getPass(renderGraph.INTERFACE).getMainScene();
  widget.addToScenes(linesScene, picksScene);
  graph.requestFrame();
}

function _removeSectionAnalysisWidgetFromScenes(widget) {
  const graph = windowManager.getWindow(windowManager.MAIN).renderGraph;
  const linesScene = graph.getInterfaceScene();
  const picksScene = graph.getPass(renderGraph.INTERFACE).getMainScene();
  widget.removeFromScenes(linesScene, picksScene);
  graph.requestFrame();
}

function _calculateTicks(mean, stdDeviation) {
  return [mean - stdDeviation, mean, mean + stdDeviation];
}

function _calculateHistogram(distance, size) {
  const minDistance = -distance;
  const maxDistance = distance;
  const range = maxDistance - minDistance;
  const histogram = new Array(size).fill(0);
  let entriesCount = 0;
  const currentDistanceMap = workspace.getMeasureByID(_currentDistanceMapId);
  for (const chunkDM of currentDistanceMap.data) {
    for (const dmValue of chunkDM) {
      if (dmValue < -distance || dmValue > distance) {
        continue;
      }

      const histogramEntryIndex = Math.round((size - 1) * (dmValue - minDistance) / range);

      histogram[histogramEntryIndex]++;
      entriesCount++;
    }
  }

  if (entriesCount === 0) {
    return histogram;
  }

  const normalization = Math.max.apply(null, histogram);
  for (let i=0; i<histogram.length; i++) {
    histogram[i] /= normalization;
  }

  return histogram;
}

function _calculateCurve(distance, mean, stdDeviation, size) {
  const minDistance = -distance;
  const maxDistance = distance;
  const range = maxDistance - minDistance;
  const step = range / size;

  const curve = new Array(size).fill(0);

  // 'k1' is the amplitude of the normal distribution curve, so limit that just below 1 for better curve appearance
  const k1 = Math.min(0.9375, 1.0 / (stdDeviation * 2.506628274631 /* sqrt(2 * PI) */));
  const k2 = -1.0 / (2 * stdDeviation * stdDeviation);

  let x = minDistance;
  for (let i = 0; i < size; i++) {
    curve[i] = (k1 * Math.exp(k2 * (x - mean) * (x - mean)));
    x += step;
  }

  return curve;
}

function _cleanDistanceMap() {
  if (_currentDistanceMapObjectId !== null) {
    const distanceMapObject = workspace.getObjectByID(_currentDistanceMapObjectId);

    _removeDistanceMapFromObject(distanceMapObject);
    _updateDistanceMapColors(null, true);
    _currentDistanceMapId = null;
    _currentDistanceMapObjectId = null;
    _currentDistanceMapHash = null;

    windowManager.getWindow(windowManager.MAIN).renderGraph.requestFrame();
    appState.setStateValue("distanceMapMode", false);
  }
}

function _cleanLines() {
  for (const widget of _lineWidgets.values()) {
    _removeLineWidgetFromScenes(widget);
  }
  _lineWidgets.clear();
}

function _cleanSections() {
  for (const widget of _sectionWidgets.values()) {
    _removeSectionWidgetFromScenes(widget);
  }
  _sectionWidgets.clear();

  for (const object of workspace.getObjects()) {
    selector.dropSelection(object);
  }
}

function _cleanSectionAnalysis() {
  for (const widget of _sectionAnalysisWidgets.values()) {
    _removeSectionAnalysisWidgetFromScenes(widget);
  }
  _sectionAnalysisWidgets.clear();
}

async function _updateVisibility() {
  for (const [linePropertyId, lineWidget] of _lineWidgets.entries()) {
    const lineObject = workspace.getMeasureByID(linePropertyId);
    if (!lineObject || !lineObject.loaded || lineObject.getHash() !== lineWidget.hash) {
      _removeLineWidgetFromScenes(lineWidget);
      _lineWidgets.delete(linePropertyId);
    }
  }
  for (const line of workspace.getMeasures().filter(measure => measure.type === objectType.LINEAR_MEASURE || measure.type === objectType.GEODESIC_MEASURE)) {
    const lineID = line.getID();
    if (!_lineWidgets.has(lineID)) {
      if (!line.getVisibility() || !line.loaded) {
        //Don't create widget until it becomes visible
        continue;
      }

      const lineWidget = new MeasureLineWidget(line);
      _lineWidgets.set(lineID, lineWidget);
      _addLineWidgetToScenes(lineWidget);
    }

    const lineWidget = _lineWidgets.get(lineID);
    lineWidget.setVisibility(line.getVisibility());
  }

  for (const [sectionGroupPropertyId, sectionGroupWidget] of _sectionWidgets.entries()) {
    const sectionObject = workspace.getMeasureByID(sectionGroupPropertyId);
    if (!sectionObject || !sectionObject.loaded || sectionObject.getHash() !== sectionGroupWidget.hash) {
      _removeSectionWidgetFromScenes(sectionGroupWidget);
      _sectionWidgets.delete(sectionGroupPropertyId);
    }
  }
  for (const sectionGroup of workspace.getMeasures().filter(measure => measure.type === objectType.SECTION)) {
    const sectionID = sectionGroup.getID();
    if (!_sectionWidgets.has(sectionID)) {
      if (!sectionGroup.getVisibility() || !sectionGroup.loaded) {
        //Don't create widget until it becomes visible
        continue;
      }

      const sectionWidget = new SectionWidget(sectionGroup);
      _sectionWidgets.set(sectionID, sectionWidget);
      _addSectionWidgetToScenes(sectionWidget);
    }

    const sectionWidget = _sectionWidgets.get(sectionID);
    sectionWidget.updateVisibility(sectionGroup);
  }

  const visibleDistanceMaps = workspace.getMeasures().filter(measure => measure.type === objectType.DISTANCE_MAP && measure.getVisibility());
  if (visibleDistanceMaps.length > 1) {
    output.error(output.API_ERROR, "Can't display more than one distance map simultaneously. Hide previous distance map.");
  }
  const visibleDistanceMap = visibleDistanceMaps[0];
  const distanceMapObject = _currentDistanceMapObjectId ? workspace.getObjectByID(_currentDistanceMapObjectId) : null;
  if (_currentDistanceMapId !== null && (!visibleDistanceMap || visibleDistanceMap.getID() !== _currentDistanceMapId || !visibleDistanceMap.loaded || _currentDistanceMapHash !== visibleDistanceMap.getHash() || !_isDistanceMapApplied(distanceMapObject))) {
    _cleanDistanceMap();
  }

  if (visibleDistanceMap && _currentDistanceMapId === null && visibleDistanceMap.loaded) {
    await _showDistanceMap(visibleDistanceMap);
  }

  //implement it with objectWrappers
  for (const [sectionAnalysisPropertyId, sectionAnalysisWidget] of _sectionAnalysisWidgets.entries()) {
    const sectionAnalysisProperty = _fakeAnalysisData.find(sectionAnalysis => sectionAnalysis.getValue("id") === sectionAnalysisPropertyId);
    if (!sectionAnalysisProperty) {
      _removeSectionAnalysisWidgetFromScenes(sectionAnalysisWidget);
      _sectionAnalysisWidgets.delete(sectionAnalysisPropertyId);
    }
  }
  for (const sectionAnalysis of _fakeAnalysisData) {
    const sectionAnalysisID = sectionAnalysis.getValue("id");

    if (_sectionAnalysisWidgets.has(sectionAnalysisID)) {
      const sectionAnalysisWidget = _sectionAnalysisWidgets.get(sectionAnalysisID);
      _removeSectionAnalysisWidgetFromScenes(sectionAnalysisWidget);
      _sectionAnalysisWidgets.delete(sectionAnalysisID);
    }

    if (!_sectionAnalysisWidgets.has(sectionAnalysisID)) {
      if (!sectionAnalysis.getValue("visible") || !sectionAnalysis.getValue("geometries")) {
        //Don't create widget until it becomes visible
        continue;
      }

      const sectionAnalysisWidget = await _createSectionAnalysisWidget(sectionAnalysis);
      _sectionAnalysisWidgets.set(sectionAnalysisID, sectionAnalysisWidget);
      _addSectionAnalysisWidgetToScenes(sectionAnalysisWidget);
    }

    const sectionAnalysisWidget = _sectionAnalysisWidgets.get(sectionAnalysisID);
    sectionAnalysisWidget.setVisibility(sectionAnalysis.getValue("visible"));
  }

  _updateScreenAppearance();
  windowManager.getWindow(windowManager.MAIN).renderGraph.requestFrame();
}

function _updateDistanceMapColors(newColors, forcedUpdate = false) {
  const distanceMapObject = workspace.getObjectByID(_currentDistanceMapObjectId);

  if (newColors && JSON.stringify(newColors) !== JSON.stringify(_savedDistanceMapColors)) {
    _savedDistanceMapColors = newColors;
    distanceMapObject.model.shader.updateDistanceMapTexture(newColors);
  }

  if (forcedUpdate) {
    distanceMapObject.model.shader.updateDistanceMapTexture(newColors);
  }
}

function _updateScreenAppearance() {
  const camera = windowManager.getWindow(windowManager.MAIN).renderGraph.getWorkspaceScene().camera;
  for (const lineWidget of _lineWidgets.values()) {
    lineWidget.updateScreenAppearance(camera);
  }
}

async function _updateSelection() {
  const graph = windowManager.getWindow(windowManager.MAIN).renderGraph;
  const measureLinesPass = graph.getPass(renderGraph.MEASURE_LINES);
  const opaqueLinesScene = measureLinesPass.getMainScene();
  const transparentLinesScene = measureLinesPass.getSecondScene();

  const sectionGroups = workspace.getMeasures().filter(measure => measure.type === objectType.SECTION);
  const selectedObjects = new Set();
  await prepareSectionSelection(sectionGroups);
  for (const sectionGroup of sectionGroups) {
    const sectionID = sectionGroup.getID();
    const sectionWidget = _sectionWidgets.get(sectionID);
    if (!sectionWidget) {
      continue;
    }

    await sectionWidget.updateSelection(sectionGroup, selectedObjects);
  }
  finishSectionSelection(selectedObjects);

  for (const line of workspace.getMeasures().filter(measure => measure.type === objectType.LINEAR_MEASURE || measure.type === objectType.GEODESIC_MEASURE)) {
    const lineID = line.getID();
    const lineWidget = _lineWidgets.get(lineID);
    if (!lineWidget) {
      continue;
    }

    lineWidget.setSelection(line.getSelection(), opaqueLinesScene, transparentLinesScene);
  }

  windowManager.getWindow(windowManager.MAIN).renderGraph.requestFrame();
}

async function _showDistanceMap(distanceMap) {
  const objectUUID = distanceMap.getProperty("measuredObjects")[0]["object-uuid"];
  const distanceMapObject = workspace.getObjectByUUID(objectUUID);
  if (!distanceMapObject) {
    output.warn("Trying to show distance map on not existed object with uuid: " + objectUUID);
    return;
  }

  if (!distanceMapObject.loaded) {
    output.warn("Distance map can't be set on unloaded object: " + distanceMapObject.getID());
    return;
  }

  if (objState.checkIsUpdating(distanceMapObject)){
    await objState.getUpdatePromise(distanceMapObject);
  }

  if (!distanceMapObject.getVisibility()) {
    output.warn("Trying to show distance map on hidden object with id: " + distanceMapObject.getID());
  }

  const distanceMapObjectChunks = distanceMapObject.frames[0].model.chunks;
  const chunkProperties = workspace.getObjectFramesProperty(distanceMapObject)[0].getValue("geometries");
  const chunkBuffers = [];
  for (let i=0; i<distanceMapObjectChunks.length; i++) {
    const chunk = distanceMapObjectChunks[i];
    const chunkID = chunk.getID();
    const dmBufferIdx = chunkProperties.findIndex(property => property.getValue("id") === chunkID);
    let dmBuffer = distanceMap.data[dmBufferIdx] ? distanceMap.data[dmBufferIdx] : null;

    const pointsCount = chunk.geometry.getVerticesCount();
    if (!dmBuffer || dmBuffer.length !== pointsCount) {
      const attrID = getAttributeIDByName("a_distance");
      const typedArray = getTypedArrayByID(attrID);

      const resizedBuffer = new typedArray(pointsCount);
      if (dmBuffer) {
        resizedBuffer.set(dmBuffer.slice(0,pointsCount));
      }

      dmBuffer = resizedBuffer;
    }
    chunkBuffers.push(dmBuffer);
  }

  addDistanceMapToObject(distanceMapObject, chunkBuffers, distanceMap);
}

function addDistanceMapToObject(targetObject, distanceMapBuffers, distanceMap) {
  _currentDistanceMapId = distanceMap.getID();
  _currentDistanceMapObjectId = targetObject.getID();
  _currentDistanceMapHash = distanceMap.getHash();

  const attrID = getAttributeIDByName("a_distance");
  const type = getTypeByID(attrID);

  for (let i=0; i<targetObject.frames[0].model.chunks.length; i++) {
    const chunk = targetObject.frames[0].model.chunks[i];
    const buffer = new GeometryBuffer({ buffer : distanceMapBuffers[i].buffer, type: type });
    chunk.geometry.setLayoutItem("a_distance", buffer);
    chunk.markAttributeDirty("a_distance");
  }

  _updateDistanceMapColors(_savedDistanceMapColors, true);
  appState.setStateValue("distanceMapMode", true);
  targetObject.updateTextureUsage();
}

function _removeDistanceMapFromObject(object) {
  if (object.type !== objectType.FUSION) {
    output.error(output.DEVELOPERS_ERROR, "trying to remove distance map to non-fusion object");
    return;
  }

  for (const chunk of object.frames[0].model.chunks) {
    chunk.geometry.deleteLayoutItem("a_distance");
  }
  object.updateTextureUsage();
}

function _isDistanceMapApplied(object) {
  for (const chunk of object.frames[0].model.chunks) {
    if (!chunk.geometry.hasLayoutItem("a_distance")) return false;
  }

  return true;
}
